// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Every modal, drawer and sheet in unfence, and the one rule they all obey.
 *
 * A USER WHO OPENS AN OVERLAY MUST ALWAYS BE ABLE TO GET OUT OF IT. There is no
 * such thing as an overlay with one way out, so each one ships FOUR, and they
 * are not alternatives:
 *
 *   1. a × control inside the panel, at least 44×44 CSS pixels of hit area
 *   2. a tap or click anywhere outside the panel
 *   3. Escape, from anywhere on the page
 *   4. and the backdrop is a REAL hit area — `position:fixed; inset:0`, below
 *      the panel, above the page, never `pointer-events:none`
 *
 * `pointerdown` rather than `click`, because it covers mouse, touch and pen in
 * one handler and fires where a synthetic click would not.
 *
 * ONE EVENT FAMILY PER CONTROL. Attaching both `touchstart` and `click` to the
 * same button makes one tap fire the handler twice: it closes and instantly
 * reopens, which the user experiences as a close button that does nothing.
 *
 * AND THE `[hidden]` GUARD IS IN THE CSS, NOT HERE. An author rule like
 * `.overlay{display:flex}` OUTRANKS the browser's `[hidden]{display:none}` —
 * author origin beats user-agent regardless of specificity — so an element
 * hidden with the attribute AND given a display by a class stays visible, and a
 * full-screen overlay then silently swallows every tap on a phone while a
 * desktop `.click()` sails straight through it and reports success. This
 * factory has shipped that bug twice. `[hidden]{display:none!important}` is in
 * main.css and a build gate asserts it survived.
 */

export interface Overlay {
  readonly element: HTMLElement;
  open(): void;
  close(): void;
  readonly isOpen: boolean;
}

const openOverlays: Overlay[] = [];

// One document-level Escape handler for all of them, so a new overlay cannot be
// added without it, and so Escape works from anywhere rather than only when the
// panel happens to hold focus.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const top = openOverlays[openOverlays.length - 1];
  if (top) {
    event.preventDefault();
    top.close();
  }
});

export interface OverlayOptions {
  /** The element that receives focus on open, for keyboard and screen-reader users. */
  readonly initialFocus?: () => HTMLElement | null;
  readonly onOpen?: () => void;
  readonly onClose?: () => void;
}

/**
 * Wire an existing element as an overlay.
 *
 * The element must contain a `[data-overlay-backdrop]` and a
 * `[data-overlay-panel]`, and it is hidden with the `hidden` attribute — the CSS
 * guard makes that authoritative.
 */
export function makeOverlay(element: HTMLElement, options: OverlayOptions = {}): Overlay {
  const backdrop = element.querySelector<HTMLElement>('[data-overlay-backdrop]');
  const panel = element.querySelector<HTMLElement>('[data-overlay-panel]');
  let previouslyFocused: HTMLElement | null = null;

  const overlay: Overlay = {
    element,
    get isOpen() {
      return !element.hasAttribute('hidden');
    },
    open() {
      if (overlay.isOpen) return;
      previouslyFocused = document.activeElement as HTMLElement | null;
      element.removeAttribute('hidden');
      openOverlays.push(overlay);
      options.onOpen?.();
      (options.initialFocus?.() ?? element.querySelector<HTMLElement>('[data-overlay-close]'))?.focus();
    },
    close() {
      if (!overlay.isOpen) return;
      element.setAttribute('hidden', '');
      const index = openOverlays.indexOf(overlay);
      if (index >= 0) openOverlays.splice(index, 1);
      options.onClose?.();
      previouslyFocused?.focus();
    },
  };

  // Exit 1 — the × control. `pointerdown` alone: adding `click` as well would
  // double-fire on a real tap.
  for (const control of element.querySelectorAll<HTMLElement>('[data-overlay-close]')) {
    control.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      overlay.close();
    });
  }

  // Exits 2 and 4 — anywhere outside the panel, including the backdrop itself.
  const outside = (event: PointerEvent) => {
    if (!panel || !panel.contains(event.target as Node)) overlay.close();
  };
  backdrop?.addEventListener('pointerdown', outside);
  element.addEventListener('pointerdown', outside);

  return overlay;
}
